import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RowSelectionState, SortingState } from "@tanstack/react-table";
import { Search } from "lucide-react";
import usePagination from "@/hooks/useSyncedPagination";
import { ActionDataBo } from "@/types/tableAction";
import {
  useGetAllExpeditionBo,
  useGetExpeditionDynamicCountsBo,
  useGetExpeditionStaticCountsBo,
} from "@/api/expedition.api";
import { ExpeditionProcessStatusEnum } from "@/constants/enums/expeditionProcessStatus.enum";
import { ExpeditionTypeEnum } from "@/constants/enums/expeditionType.enum";
import type { GetAllExpeditionBoDto } from "@/types/api/api-types";
import { ExpeditionsBoxTable } from "@/components/backOffice/expeditionsBox/expeditionsBoxTable";
import { AssigneeFilter } from "@/components/backOffice/expeditionsBox/assigneeFilter";
import { PendingExpeditionActionModals } from "@/components/backOffice/pendingExpeditions/pendingExpeditionActionModals";
import { WeekFilter } from "@/components/backOffice/expeditionsBox/weekFilter";
import { ExpeditionsBoxFilters } from "@/components/backOffice/expeditionsBox/expeditionsBoxFilters";
import { useExpeditionsBoxFilters } from "@/components/backOffice/expeditionsBox/useExpeditionsBoxFilters.reducer";
import { dateRangeToParams } from "@/components/backOffice/shared/dateFilter.util";
import { BulkActionsBar } from "@/components/backOffice/expeditionsBox/bulkActionsBar";
import { UnifiedInput } from "@/components/ui/customs/unified-input";
import { userAtom } from "@/stores/authStore";
import { useAtomValue } from "jotai";
import { COUNTRY_REGIONS } from "@/constants/countries";

const STATIC_COUNTS_QUERY_FILTER: GetAllExpeditionBoDto = {
  processStatus: [ExpeditionProcessStatusEnum.PENDING_PELICAN],
};

export const ExpeditionsBoxPage = () => {
  const { t } = useTranslation("boExpeditions");
  const [actionData, setActionData] = useState<ActionDataBo | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = usePagination("expeditionsBox");
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const user = useAtomValue(userAtom);

  const { filters, debouncedSearch, actions } = useExpeditionsBoxFilters();

  const dateFilter = useMemo(
    () => dateRangeToParams("pickupDate", filters.pickupDateAt),
    [filters.pickupDateAt],
  );

  const quickFiltersQueryFilter = useMemo<GetAllExpeditionBoDto>(
    () => ({
      processStatus: [ExpeditionProcessStatusEnum.PENDING_PELICAN],
      ...dateFilter,
      ...(filters.isCustomQuotation ? { quotationType: ExpeditionTypeEnum.CUSTOM_QUOTATION } : {}),
      ...(filters.isLate ? { isLate: true } : {}),
      ...(filters.isExpress ? { isExpress: true } : {}),
      ...(filters.isRemote ? { isRemote: true } : {}),
      ...(filters.isDelayed ? { deliveryFromDateAfter: "1970-01-01T00:00:00.000Z" } : {}),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(filters.searchExact ? { searchExact: true } : {}),
    }),
    [
      dateFilter,
      filters.isCustomQuotation,
      filters.isLate,
      filters.isExpress,
      filters.isRemote,
      filters.isDelayed,
      debouncedSearch,
      filters.searchExact,
    ],
  );

  const additionalFilters = useMemo(
    () => ({
      ...(filters.country
        ? { country: filters.country }
        : filters.region
          ? { countries: COUNTRY_REGIONS[filters.region] }
          : {}),
      ...(filters.draftMode === "only"
        ? { isDraft: true }
        : filters.draftMode === "onlyMine"
          ? { isDraftBySelf: true }
          : filters.draftMode === "overdue48h"
            ? { isDraftOverdue: true }
            : filters.draftMode === "exclude"
              ? { isDraft: false }
              : {}),
      ...(filters.customId ? { customId: filters.customId } : {}),
      ...(filters.pickupZoneId ? { pickupZoneId: filters.pickupZoneId } : {}),
      ...(filters.packagingType ? { packagingType: filters.packagingType } : {}),
      ...(filters.deliveryFromDateAfter
        ? { deliveryFromDateAfter: filters.deliveryFromDateAfter }
        : {}),
      ...(filters.deliveryFromDateBefore
        ? { deliveryFromDateBefore: filters.deliveryFromDateBefore }
        : {}),
      ...(filters.isReExpedition !== null ? { isReExpedition: filters.isReExpedition } : {}),
    }),
    [
      filters.country,
      filters.region,
      filters.draftMode,
      filters.customId,
      filters.pickupZoneId,
      filters.packagingType,
      filters.deliveryFromDateAfter,
      filters.deliveryFromDateBefore,
      filters.isReExpedition,
    ],
  );

  const dynamicCountsQueryFilter = useMemo<GetAllExpeditionBoDto>(
    () => ({ ...quickFiltersQueryFilter, ...additionalFilters }),
    [quickFiltersQueryFilter, additionalFilters],
  );

  const assigneeFilter = useMemo(() => {
    if (filters.assigneeId === "unassigned") return { isUnassigned: true };
    if (filters.assigneeId) return { assignedToId: filters.assigneeId };
    if (filters.assignmentMode === "mine" && user?.userId) return { assignedToId: user.userId };
    return {};
  }, [filters.assigneeId, filters.assignmentMode, user?.userId]);

  const tableQueryFilter = useMemo<GetAllExpeditionBoDto>(
    () => ({ ...dynamicCountsQueryFilter, ...assigneeFilter }),
    [dynamicCountsQueryFilter, assigneeFilter],
  );

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    setRowSelection({});
  }, [tableQueryFilter, setPagination]);

  const { data: staticCounts } = useGetExpeditionStaticCountsBo(STATIC_COUNTS_QUERY_FILTER);
  const { data: dynamicCounts } = useGetExpeditionDynamicCountsBo(dynamicCountsQueryFilter);

  const { data: expeditions, isLoading: isExpeditionsLoading } = useGetAllExpeditionBo({
    ...tableQueryFilter,
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
    sortBy: sorting[0]?.id,
    sortOrder: sorting[0]?.desc ? "DESC" : "ASC",
  });

  const expeditionsData = useMemo(() => expeditions?.data ?? [], [expeditions]);

  const selectedExpeditions = useMemo(
    () =>
      Object.keys(rowSelection)
        .filter((key) => rowSelection[key])
        .map((key) => expeditionsData[parseInt(key)])
        .filter(Boolean),
    [rowSelection, expeditionsData],
  );

  return (
    <div className="bg-border/20 flex h-fit min-h-dvh w-full flex-1 flex-col gap-6 p-4 sm:p-8">
      <div className="flex w-full flex-col justify-between gap-2 md:flex-row">
        <div className="flex w-fit flex-col gap-2">
          <h1 className="font-jakarta-sans text-3xl font-semibold">{t("boxPage.title")}</h1>
          <p>{t("boxPage.subtitle")}</p>
          <span className="font-bold text-red-500">
            {t("boxPage.totalLate", { total: staticCounts?.totalLate ?? "" })}
          </span>
        </div>
        <div className="w-72">
          <UnifiedInput
            inputType="text"
            leftElement={<Search />}
            value={filters.search}
            onChange={(e) => actions.setSearch(String(e || ""))}
            className="placeholder:text-[#94A3B8]"
            placeholder={t("boxPage.searchPlaceholder")}
          />
        </div>
      </div>
      <ExpeditionsBoxFilters
        filters={filters}
        actions={actions}
        myUserId={user?.userId}
        quickCounts={dynamicCounts}
      />
      <div className="rounded-xl border-1 bg-white p-4 sm:p-8">
        <WeekFilter
          currentWeekStart={filters.weekStart}
          onWeekStartChange={actions.setWeekStart}
          dateAt={filters.pickupDateAt}
          dateFilterType={filters.pickupDateFilterType}
          onDaySelect={actions.setPickupDay}
          onWeekSelect={actions.setPickupWeek}
          onRangeSelect={actions.setPickupDateRange}
        />
      </div>
      {/* <div className="rounded-xl border-1 bg-white p-4 sm:p-8"> */}
      <div className="mt-2 space-y-2">
        <div className="flex flex-col justify-end gap-4 xl:flex-row xl:items-center">
          <AssigneeFilter
            selectedAssigneeId={filters.assigneeId}
            onSelect={actions.setAssigneeId}
            byAssignee={staticCounts?.byAssignee}
            myUserId={user?.userId ?? ""}
            restrictToUserId={filters.assignmentMode === "mine" ? user?.userId : undefined}
            draftMode={filters.draftMode}
            draftCounts={staticCounts}
            onDraftModeChange={actions.setDraftMode}
          />
        </div>
        <ExpeditionsBoxTable
          data={expeditionsData}
          rowCount={expeditions?.count || 0}
          pagination={pagination}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          setActionData={setActionData}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          isLoading={isExpeditionsLoading}
        />
      </div>
      <BulkActionsBar
        selectedExpeditions={selectedExpeditions}
        onSuccess={() => setRowSelection({})}
      />
      <PendingExpeditionActionModals actionData={actionData} setActionData={setActionData} />
    </div>
  );
};
